// Approve one pallet at inspection — one mutation per card, so each card shows
// its own tick (UF-02: the confirmation lands on the card, not over the whole
// screen, ~60 times a day) and two quick approvals never share one spinner.
//
// The write is today's (Inspection.tsx approveMutation): version-checked, so
// two inspectors can't race the same pallet to opposing outcomes, and it
// records the result as well as the status — the trigger stamps inspected_at,
// the result is ours, as the Pallets page does, or a pass reads like a fail
// (Paltrack CE vs UI). A refusal answers in today's "Approval blocked" dialog,
// with today's text — except the QC-FAIL hold, which reads in plain words
// (QC_HOLD_DOMAIN).
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { updateWithVersion, isStaleWriteError } from '@/lib/optimistic'
import { ValidationError } from '@/lib/errorReporter'
import { alertDialog } from '@/components/Dialogs'
import { qcHoldRefusal } from '@/lib/packhouseErrorDomains'

export interface ApprovablePallet { pallet_id: string; version: number }

export const STALE_PALLET_MESSAGE = 'This pallet was just updated by someone else. Refresh the queue and try again.'

/**
 * One inspection verdict on a pallet — Approve here, Reject in
 * useRejectPallet.ts. Version-checked; a stale write gets today's sentence,
 * anything else is thrown as it came (today's behaviour).
 */
export async function writeVerdict(
  pallet: ApprovablePallet,
  patch: (inspectorId: string) => Record<string, unknown>,
): Promise<void> {
  const { data: { session } } = await supabase.auth.getSession()
  const user = session?.user
  if (!user) throw new ValidationError('Not authenticated')
  try {
    await updateWithVersion('pallets', { col: 'pallet_id', value: pallet.pallet_id }, pallet.version, patch(user.id))
  } catch (e: unknown) {
    if (isStaleWriteError(e)) throw new ValidationError(STALE_PALLET_MESSAGE)
    throw e
  }
}

export function approvePallet(pallet: ApprovablePallet): Promise<void> {
  return writeVerdict(pallet,
    inspectorId => ({ status: 'INSPECTED_APPROVED', inspected_by: inspectorId, inspection_result: 'APPROVED' }))
}

/**
 * The QC-FAIL approve gate's refusal (mig 20260703122000: 23514, "… draws from
 * a run that FAILED a critical QC check …"). Matched on the text as well as
 * the code — a wrapper can drop `.code`, and 23514 is any check constraint.
 */
export function isQcApproveRefusal(e: unknown): boolean {
  const msg = (e as { message?: unknown } | null)?.message
  return typeof msg === 'string' && /FAILED a critical QC check/i.test(msg)
}

/**
 * One card's Approve. `onApproved` runs even if the card has unmounted by
 * then (it is an option of the mutation, not of the mutate call) — the page
 * passes its useHeldVerdicts `hold`, so the tick has somewhere to land.
 * `onQcBlocked` answers the QC gate's refusal instead of the plain dialog
 * (F03 UF-05: one choice — open the failed check, or close).
 */
export function useApprovePallet<T extends ApprovablePallet>(
  onApproved?: (pallet: T) => void,
  onQcBlocked?: (pallet: T, e: Error) => void,
) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pallet: T) => approvePallet(pallet),
    onSuccess: (_d, pallet) => {
      onApproved?.(pallet)
      void qc.invalidateQueries({ queryKey: ['inspection-dashboard'] })
      // A pallet already with PPECB keeps Approve on its consignment's list
      // until UF-20 removes it; that list must show the new status too.
      void qc.invalidateQueries({ queryKey: ['inspection-consignment-pallets'] })
    },
    // The QC-FAIL approve gate (mig 20260703122000) refuses approval when the
    // pallet draws from an undispositioned FAIL run (23514) with an operator-
    // friendly message — surface it. A manager releases it via the QC run's
    // disposition.
    onError: (e: Error, pallet) => {
      if (onQcBlocked && isQcApproveRefusal(e)) { onQcBlocked(pallet, e); return }
      void alertDialog({ title: 'Approval blocked', message: qcHoldRefusal(e) ?? e.message, tone: 'danger' })
    },
  })
}
